import React from 'react';
import { Runbook } from '../types';

interface DetailViewProps {
  runbook: Runbook;
  isUnlocked: boolean;
  onOpenCheckout: () => void;
  onOpenReader: (slug: string) => void;
  onBackToCatalog: () => void;
}

export const DetailView: React.FC<DetailViewProps> = ({
  runbook,
  isUnlocked,
  onOpenCheckout,
  onOpenReader,
  onBackToCatalog,
}) => {
  return (
    <div className="space-y-8">
      <button
        onClick={onBackToCatalog}
        className="text-xs font-mono text-[#8b92a5] hover:text-white flex items-center gap-1 transition"
      >
        &larr; BACK TO CATALOG
      </button>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Left: Technical Spec Card & Checkout Trigger */}
        <div className="lg:col-span-5 space-y-6">
          <div className="blueprint-card rounded-xl p-6 border border-sky-500/30 space-y-6 relative overflow-hidden">
            <div className="flex items-center justify-between text-xs font-mono text-[#8b92a5]">
              <span className="text-sky-400 font-bold">{runbook.code}</span>
              <span className="uppercase">{runbook.category}</span>
            </div>

            <div className="space-y-2">
              <h2 className="font-tech text-2xl sm:text-3xl font-bold text-white leading-tight">
                {runbook.title}
              </h2>
              <p className="text-xs text-[#8b92a5] font-mono italic">
                {runbook.subtitle}
              </p>
            </div>

            <div className="border-t border-[#232736] pt-4 flex items-baseline justify-between">
              <div>
                <span className="text-[10px] font-mono text-[#8b92a5] uppercase block">
                  DIRECT UNLOCK
                </span>
                <span className="font-mono text-3xl font-bold text-white">
                  ${runbook.price}
                </span>
              </div>
              <span className="text-[11px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded">
                INSTANT ACCESS
              </span>
            </div>

            <div>
              {isUnlocked ? (
                <button
                  onClick={() => onOpenReader(runbook.slug)}
                  className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 text-black font-tech font-bold text-sm rounded transition flex items-center justify-center gap-2"
                >
                  ACCESS PROTOCOL IN READER &rarr;
                </button>
              ) : (
                <button
                  onClick={onOpenCheckout}
                  className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-black font-tech font-bold text-sm rounded shadow-[0_0_15px_rgba(56,189,248,0.3)] active:scale-[0.98] transition flex items-center justify-center gap-2"
                >
                  ACQUIRE RUNBOOK (${runbook.price}) &rarr;
                </button>
              )}
            </div>
          </div>

          <div className="p-4 rounded-lg bg-[#10121a] border border-[#232736] space-y-2 text-xs text-[#8b92a5] font-mono">
            <div className="flex justify-between">
              <span>FORMAT:</span> <span className="text-white">Markdown + Print PDF</span>
            </div>
            <div className="flex justify-between">
              <span>DELIVERY:</span> <span className="text-white">Direct in-browser unlock</span>
            </div>
            <div className="flex justify-between">
              <span>LICENSE:</span> <span className="text-white">Commercial personal use</span>
            </div>
          </div>
        </div>

        {/* Right: Syllabus, Core Value, & Objection Defenses */}
        <div className="lg:col-span-7 space-y-8">
          <div>
            <div className="inline-block px-2 py-0.5 rounded bg-[#181b26] border border-[#232736] text-[11px] font-mono text-sky-400 uppercase">
              DOMAIN // {runbook.niche}
            </div>
            <p className="text-lg text-zinc-200 font-tech font-medium mt-3 leading-relaxed">
              {runbook.promise}
            </p>
            <p className="text-xs text-[#8b92a5] font-mono mt-2 leading-relaxed">
              {runbook.blurb}
            </p>
          </div>

          {/* Included Protocols */}
          <div className="border-t border-[#232736] pt-6 space-y-3">
            <h3 className="text-xs font-mono uppercase tracking-wider text-sky-400">
              Included Protocols & Deliverables
            </h3>
            <ul className="space-y-2.5">
              {runbook.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-xs font-mono text-zinc-300">
                  <span className="text-sky-400">&#x25b8;</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Table of Contents */}
          <div className="border-t border-[#232736] pt-6 space-y-3">
            <h3 className="text-xs font-mono uppercase tracking-wider text-sky-400">
              Runbook Schema
            </h3>
            <ol className="space-y-2 font-mono text-xs text-zinc-300">
              {runbook.sections.map((sec, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between py-1.5 border-b border-[#232736]"
                >
                  <span>{sec.heading}</span>
                  <span className="text-[10px] text-[#525970]">
                    {isUnlocked ? 'AVAILABLE' : 'LOCKED'}
                  </span>
                </li>
              ))}
            </ol>
          </div>

          {/* Target Operator & FAQs */}
          <div className="border-t border-[#232736] pt-6 space-y-4">
            <div>
              <h4 className="text-xs font-mono uppercase text-[#8b92a5]">Target Operator</h4>
              <p className="text-xs text-zinc-300 mt-1">{runbook.audience}</p>
            </div>
            <div className="space-y-3 pt-2">
              {runbook.faqs.map((f, i) => (
                <div key={i} className="border-t border-[#232736] pt-2.5">
                  <div className="text-xs font-mono text-white font-bold">{f.q}</div>
                  <div className="text-xs font-mono text-[#8b92a5] mt-1">{f.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
